import { get, post, put, del } from '@/services/http';
import { SupplierModel } from './supplier';
import { StoreLocationModel } from './store_location';
import { FilterColumnList } from '@/collections/filter_columns';
import { FilterColumnModel } from '@/models/filter_column';
import { EventHandler } from '@/modules/events';
import { StoreModel } from '@/models/store';

/**
 * Interface for the schedule configuration object.
 */
export interface IScheduleConfig {
  hour?: number;        // 0-23 (for all schedule types)
  dayOfWeek?: number;   // 0-6 (Sunday-Saturday, for weekly only)
  dayOfMonth?: number;  // 1-31 or -1 for last day (for monthly only)
  timezone?: string;    // User's timezone
}

/**
 * Interface for a single filter column criterion.
 */
export interface IFilterColumn {
  code: string;
  operator: string;
  value: any[];
}

/**
 * Represents the full data structure for an automated purchase order configuration.
 */
export interface IAutomationConfig {
  id?: number;
  name: string;
  merchantId: number;
  supplierId: number | null;
  locationIds: number[];                 // Multiple filter locations
  deliveryLocationId: number | null;     // Single delivery location
  scheduleType: 'daily' | 'weekly' | 'monthly';
  scheduleConfig: IScheduleConfig;
  filterColumns: IFilterColumn[];
  productSegmentId: number | null;
  minOrderValue: number | null;
  maxOrderValue: number | null;
  emailRecipients: string[];
  isActive: boolean;
  isDeleted?: boolean;   // Task 3.1: Soft delete support
  supplier?: SupplierModel;
  deliveryLocation?: StoreLocationModel;
  lastRunTime?: string;
  nextRunTime?: string;
  runCount?: number;
  lastRunStatus?: string;
  lastRunResult?: any;
  lastRunError?: string;
  createdAt?: string;
  updatedAt?: string;
  eventHandler?: EventHandler; // Optional event handler for analytics
}

/**
 * Manages the state and API interactions for a single Automated PO Configuration.
 * This class handles creating, updating, deleting, and toggling the configuration's status.
 */
export class AutomationConfigModel {

  /**
   * Checks if the model represents a new configuration that hasn't been saved yet.
   * @returns {boolean} True if the configuration is new, false otherwise.
   */
  public get isNew(): boolean {
    return !this.id;
  }
  /** The unique identifier of the configuration. */
  public id?: number;

  /** The user-defined name for the configuration. */
  public name: string = '';

  /** The ID of the merchant this configuration belongs to. */
  public merchantId: number = 0;

  /** Optional: The ID of the supplier to restrict this configuration to. */
  public supplierId: number | null = null;

  /** An array of location IDs for inventory filtering. */
  public locationIds: number[] = [];

  /** Optional: The single location ID for PO delivery. */
  public deliveryLocationId: number | null = null;

  /** The type of schedule (e.g., 'daily', 'weekly', 'monthly'). */
  public scheduleType: 'daily' | 'weekly' | 'monthly' = 'daily';

  /** The detailed configuration for the schedule (e.g., time, day). */
  public scheduleConfig: IScheduleConfig = {};

  /** A list of product filters to apply. */
  public filterColumns: FilterColumnList = new FilterColumnList();

  /** The ID of a product segment to use for filtering. */
  public productSegmentId: number | null = null;

  /** Optional: The minimum total value for a PO to be generated. */
  public minOrderValue: number | null = null;

  /** Optional: The maximum total value for a generated PO. */
  public maxOrderValue: number | null = null;

  /** A list of email addresses to receive notifications. */
  public emailRecipients: string[] = [];

  /** Whether the configuration is currently active and should run on schedule. */
  public isActive: boolean = true;

  /** Whether the configuration has been soft deleted. Task 3.1: Soft delete support */
  public isDeleted: boolean = false;

  /** The timestamp of the last time the automation ran. */
  public lastRunTime?: string;

  /** The calculated timestamp for the next scheduled run. */
  public nextRunTime?: string;

  /** The total number of times the automation has run. */
  public runCount: number = 0;

  /** The status of the last run (e.g., 'completed', 'failed'). */
  public lastRunStatus?: string;

  /** The result data from the last run (e.g., created PO IDs). */
  public lastRunResult?: any;

  /** Any error message from the last run if it failed. */
  public lastRunError?: string;

  /** The associated Supplier model, if `supplierId` is set. */
  public supplier?: SupplierModel;

  /** The associated Location model for the delivery location. */
  public deliveryLocation?: StoreLocationModel;

  /** The creation timestamp of the configuration. */
  public createdAt?: string;

  /** The last update timestamp of the configuration. */
  public updatedAt?: string;

  /** Event handler for tracking analytics. */
  private eventHandler: EventHandler;

  /**
   * Constructs an instance of AutomationConfigModel.
   * @param {Partial<IAutomationConfig>} [data={}] - Initial data to populate the model.
   */
  constructor(data: Partial<IAutomationConfig> = {}) {
    // Initialize event handler with provided instance or create default one
    this.eventHandler = data.eventHandler || new EventHandler({ store: new StoreModel() });
    this.update(data);
  }

  /**
   * Updates the model's properties from a partial data object.
   * @param {Partial<IAutomationConfig>} data - The data to update the model with.
   */
  public update(data: Partial<IAutomationConfig>) {
    if (data.id !== undefined) { this.id = data.id; }
    if (data.name !== undefined) { this.name = data.name || ''; }
    if (data.merchantId !== undefined) { this.merchantId = data.merchantId; }
    if (data.supplierId !== undefined) { this.supplierId = data.supplierId; }
    if (data.locationIds !== undefined) {
      // Ensure locationIds are numbers
      this.locationIds = (data.locationIds || []).map((id) => {
        const parsed = parseInt(String(id), 10);
        return isNaN(parsed) ? 0 : parsed;
      });
    }
    if (data.deliveryLocationId !== undefined) { this.deliveryLocationId = data.deliveryLocationId || null; }
    if (data.scheduleType !== undefined) { this.scheduleType = data.scheduleType || 'daily'; }
    if (data.scheduleConfig !== undefined) { this.scheduleConfig = data.scheduleConfig || {}; }
    if (data.filterColumns) {
      this.filterColumns.mapData(data.filterColumns);
    }
    if (data.productSegmentId !== undefined) { this.productSegmentId = data.productSegmentId || null; }
    if (data.minOrderValue !== undefined) { this.minOrderValue = data.minOrderValue; }
    if (data.maxOrderValue !== undefined) { this.maxOrderValue = data.maxOrderValue; }
    if (data.emailRecipients !== undefined) { this.emailRecipients = data.emailRecipients; }
    if (data.isActive !== undefined) { this.isActive = data.isActive; }
    if (data.isDeleted !== undefined) { this.isDeleted = data.isDeleted; } // Task 3.1: Handle soft delete field
    if (data.lastRunTime !== undefined) { this.lastRunTime = data.lastRunTime; }
    if (data.nextRunTime !== undefined) { this.nextRunTime = data.nextRunTime; }
    if (data.runCount !== undefined) { this.runCount = data.runCount; }
    if (data.lastRunStatus !== undefined) { this.lastRunStatus = data.lastRunStatus; }
    if (data.lastRunResult !== undefined) { this.lastRunResult = data.lastRunResult; }
    if (data.lastRunError !== undefined) { this.lastRunError = data.lastRunError; }

    // Handle supplier - convert plain object to SupplierModel instance
    if (data.supplier !== undefined) {
      if (data.supplier instanceof SupplierModel) {
        this.supplier = data.supplier;
      } else if (data.supplier) {
        this.supplier = new SupplierModel(data.supplier);
      } else {
        this.supplier = undefined;
      }
    }

    // Handle deliveryLocation - convert plain object to StoreLocationModel instance
    if (data.deliveryLocation !== undefined) {
      if (data.deliveryLocation instanceof StoreLocationModel) {
        this.deliveryLocation = data.deliveryLocation;
      } else if (data.deliveryLocation) {
        const location = new StoreLocationModel();
        location.mapData(data.deliveryLocation);
        this.deliveryLocation = location;
      } else {
        this.deliveryLocation = undefined;
      }
    }

    if (data.createdAt !== undefined) { this.createdAt = data.createdAt; }
    if (data.updatedAt !== undefined) { this.updatedAt = data.updatedAt; }
  }

  /**
   * Saves the current configuration to the server.
   * Creates a new record if it's a new configuration, otherwise updates the existing one.
   * @returns {Promise<void>}
   */
  public async save(): Promise<void> {
    const url = this.id ? `/po-automation/configs/${this.id}` : '/po-automation/configs';
    const method = this.id ? put : post;

    // Handle filterColumns - it might be an array or FilterColumnList
    let filterColumnsData: IFilterColumn[] = [];
    if (this.filterColumns instanceof FilterColumnList) {
      filterColumnsData = this.filterColumns.items.map((item) => item.toJSON());
    }

    // Ensure locationIds are integers, not strings
    const locationIdsAsIntegers = this.locationIds.map((id) => {
      const parsed = parseInt(String(id), 10);
      if (isNaN(parsed)) {
        throw new Error(`Invalid location ID: ${id}`);
      }
      return parsed;
    });

    const payload = {
      name: this.name,
      supplierId: this.supplierId,
      locationIds: locationIdsAsIntegers,
      deliveryLocationId: this.deliveryLocationId,
      scheduleType: this.scheduleType,
      scheduleConfig: this.scheduleConfig,
      filterColumns: filterColumnsData,
      productSegmentId: this.productSegmentId,
      minOrderValue: this.minOrderValue,
      maxOrderValue: this.maxOrderValue,
      emailRecipients: this.emailRecipients,
      isActive: this.isActive,
    };

    const wasNew = this.isNew;
    const response: any = await method(url, payload);
    this.update(response.data);

    // Track configuration creation event (only for new configurations)
    if (wasNew) {
      this.eventHandler.trackAutomatedPurchaseOrderStep('Configuration Created', {
        entry_point: 'automation_config_form',
        config_id: this.id,
        schedule_type: this.scheduleType,
        filter_count: Array.isArray(this.filterColumns) ? this.filterColumns.length : this.filterColumns.items?.length || 0,
        min_order_value: this.minOrderValue,
        max_order_value: this.maxOrderValue,
        supplier_id: this.supplierId,
        location_count: this.locationIds.length,
        email_recipient_count: this.emailRecipients.length,
        has_product_segment: !!this.productSegmentId,
      });
    }
  }

  /**
   * Deletes the configuration from the server.
   * @returns {Promise<void>}
   * @throws {Error} If the configuration does not have an ID (i.e., it hasn't been saved).
   */
  public async delete(): Promise<void> {
    if (!this.id) { throw new Error('Cannot delete unsaved configuration'); }
    await del(`/po-automation/configs/${this.id}`);
  }

  /**
   * Toggles the `isActive` status of the configuration on the server.
   * @returns {Promise<void>}
   * @throws {Error} If the configuration does not have an ID.
   */
  public async toggle(): Promise<void> {
    if (!this.id) { throw new Error('Cannot toggle unsaved configuration'); }
    const response: any = await post(`/po-automation/configs/${this.id}/toggle`);
    this.update(response.data);
  }

  /**
   * Triggers a manual run of the configuration (test run).
   * @returns {Promise<any>} The response data from the server.
   * @throws {Error} If the configuration does not have an ID.
   */
  public async testRun(): Promise<any> {
    if (!this.id) { throw new Error('Cannot run unsaved configuration'); }
    const response: any = await post(`/po-automation/configs/${this.id}/test-run`);
    return response.data;
  }

  /**
   * Converts the model instance to a plain JSON object, suitable for API payloads.
   * @returns {any} A JSON-compatible representation of the model.
   */
  public toJSON(): any {
    let filterColumnsData: IFilterColumn[] = [];
    if (this.filterColumns instanceof FilterColumnList) {
      filterColumnsData = this.filterColumns.items.map((item) => item.toJSON());
    }

    return {
      id: this.id,
      name: this.name,
      merchantId: this.merchantId,
      supplierId: this.supplierId,
      locationIds: this.locationIds,
      deliveryLocationId: this.deliveryLocationId,
      scheduleType: this.scheduleType,
      scheduleConfig: this.scheduleConfig,
      filterColumns: filterColumnsData,
      productSegmentId: this.productSegmentId,
      minOrderValue: this.minOrderValue,
      maxOrderValue: this.maxOrderValue,
      emailRecipients: this.emailRecipients,
      isActive: this.isActive,
      isDeleted: this.isDeleted, // Task 3.1: Include soft delete field in JSON serialization
      supplier: this.supplier ? (this.supplier.toJSON ? this.supplier.toJSON() : this.supplier) : undefined,
      deliveryLocation: this.deliveryLocation ? { id: this.deliveryLocation.id, name: this.deliveryLocation.name } : undefined,
      lastRunTime: this.lastRunTime,
      nextRunTime: this.nextRunTime,
      runCount: this.runCount,
      lastRunStatus: this.lastRunStatus,
      lastRunResult: this.lastRunResult,
      lastRunError: this.lastRunError,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }
}
